import { Instance, types } from 'mobx-state-tree';
import { FieldState, FormState, Validator } from 'formstate';
import { matchesRegExp } from '../../../shared/validators/RegularExpressionValidator';
import { required } from '../../../shared/validators/RequiredValidator';
import { noDuplicateTaxIdValidator } from '../../validator/NoDuplicateTaxIdValidator';
import { replaceFieldWithDashes } from '../TaxIdFormatter';
import { malaysiaMyKadValidator } from '../../validator/MalaysiaMYKadValidator'
import { enrollmentResource } from '../../../../../infrastructure/http/EnrollmentResource';
import { flow } from 'mobx';
import { EnrollmentRootStore } from '../../../../root/EnrollmentRootStore';
import { getRootStore } from '../../../../root/RootStoreUtils';
import { validateIndonesianNPWPAgainstGovID } from '../../validator/ValidateIndonesianNPWPAgainstGovID';

export const IndividualTaxEntry = types
  .model('IndividualTaxEntry', {
    regularExpression: types.string,
    isRequired: types.boolean,
    isUniqueRequired: types.optional(types.boolean, false),
    taxId: types.maybe(types.string),
    taxIdTypeId: types.frozen(types.number),
    mask: types.string,
    name: types.string,
    canCollect: types.boolean,
    countryIsoCode: types.string
  })
  .volatile(() => ({
    _reactForm: null as FormState<{ taxId: FieldState<string> }> | null
  }))
  .views(self => ({

    get rootStore(): EnrollmentRootStore {
      return getRootStore(self);
    },
    get validators() {

      const validators: Validator<any>[] = [];
      if (self.isRequired) {
        validators.push(required());
      }
      if (self.countryIsoCode === 'ID') {
        const governmentId = this.rootStore.moduleStores.governmentEntry?.governmentId || "";
        validators.push(async (value: string) => {
          return Promise.resolve(
            validateIndonesianNPWPAgainstGovID(value, governmentId)
          );
        })
      }
      if (self.countryIsoCode === 'MY') {
        const ageEntry = this.rootStore.moduleStores.ageEntry;
        const dob = `${ageEntry?.birthYear}-${String(ageEntry?.birthMonth).padStart(2, "0")}-${String(ageEntry?.birthDay).padStart(2, "0")}`;
        validators.push(async (value: string) => {
          return Promise.resolve(
            malaysiaMyKadValidator(value, dob, value)
          );
        })
      };
      validators.push(matchesRegExp(self.regularExpression, 'invalidTaxId'));
      if (self.isUniqueRequired) {
        validators.push((value: string) =>
          value !== self.taxId ? noDuplicateTaxIdValidator(value, self.taxIdTypeId) : false
        );
      }
      return validators;
    },
    get reactForm(): FormState<{ taxId: FieldState<string> }> {
      return self._reactForm!;
    },
    get haveValidTaxId() {
      return this.reactForm.$.taxId.value && !this.reactForm.$.taxId.hasError;
    },
    get taxIdField(): FieldState<string> {
      return this.reactForm.$.taxId;
    },
    get requireTaxId(): boolean {
      return self.isRequired;
    }
  }))
  .actions(self => ({
    fetchEnrollmentStep: flow(function* getEnrollmentResource() : any {
      const response = yield enrollmentResource.get('steps/2');

      return response.data;
    }),
    fixDashes() {
      replaceFieldWithDashes(self.reactForm.$.taxId, self.mask,self.countryIsoCode);
    },
    afterCreate() {
      self._reactForm = new FormState({
        taxId: new FieldState(self.taxId || '').validators(...self.validators)
      });
      this.fixDashes();
    }
  }));

export type IndividualTaxEntry = Instance<typeof IndividualTaxEntry>;
